import React, { useState, useMemo } from 'react';
import { Card, Table, Select, Button, message, Spin, Typography } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api.service';
import {
  itemClassificationGlApi,
  getStageLabel,
  getClassificationLabel,
  type ClassificationGlRow,
  type ClassificationGlRowWithId,
} from '@/services/itemClassificationGl.service';

const { Title, Text } = Typography;

interface CoaAccount {
  id: string;
  account_code: string;
  account_name: string;
  is_group: boolean;
}

export const ItemClassificationGlPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [cellValues, setCellValues] = useState<Record<string, string>>({});

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['item-classification-gl'],
    queryFn: () => itemClassificationGlApi.get(),
  });

  const { data: accountsResponse } = useQuery({
    queryKey: ['chart-of-accounts-flat'],
    queryFn: async () => {
      const res = await api.get<any>('/finance/chart-of-accounts');
      const body = res?.data ?? res;
      const list = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
      return list as CoaAccount[];
    },
  });

  const flatAccounts = useMemo(() => {
    const list = Array.isArray(accountsResponse) ? accountsResponse : [];
    return list.filter((a: CoaAccount) => !a.is_group);
  }, [accountsResponse]);

  const rows = config?.rows ?? [];
  const classifications = config?.classifications ?? [];
  const stages = config?.stages ?? [];

  const keyToAccountId = useMemo(() => {
    const m: Record<string, string> = {};
    rows.forEach((r: ClassificationGlRowWithId) => {
      m[`${r.classification}|${r.stage}`] = r.account_id;
    });
    return m;
  }, [rows]);

  const currentCellValues = useMemo(() => {
    const m: Record<string, string> = { ...keyToAccountId };
    Object.keys(cellValues).forEach((k) => {
      if (cellValues[k]) m[k] = cellValues[k];
    });
    return m;
  }, [keyToAccountId, cellValues]);

  const updateCell = (classification: string, stage: string, accountId: string) => {
    setCellValues((prev) => ({ ...prev, [`${classification}|${stage}`]: accountId }));
  };

  const saveMutation = useMutation({
    mutationFn: (payload: ClassificationGlRow[]) => itemClassificationGlApi.put(payload),
    onSuccess: () => {
      message.success('Item classification GL accounts saved');
      setCellValues({});
      queryClient.invalidateQueries({ queryKey: ['item-classification-gl'] });
    },
    onError: (err: any) => {
      message.error(err?.response?.data?.message ?? err?.message ?? 'Failed to save');
    },
  });

  const handleSave = () => {
    const payload: ClassificationGlRow[] = [];
    classifications.forEach((c) => {
      stages.forEach((s) => {
        const accountId = currentCellValues[`${c}|${s}`];
        if (accountId) payload.push({ classification: c, stage: s, account_id: accountId });
      });
    });
    saveMutation.mutate(payload);
  };

  const columns = [
    {
      title: 'Classification',
      dataIndex: 'classification',
      key: 'classification',
      width: 180,
      render: (c: string) => getClassificationLabel(c),
    },
    ...stages.map((stage) => ({
      title: getStageLabel(stage),
      key: stage,
      dataIndex: stage,
      width: 220,
      render: (_: unknown, record: { classification: string }) => (
        <Select
          placeholder="Select account"
          allowClear
          style={{ width: '100%' }}
          value={currentCellValues[`${record.classification}|${stage}`] || undefined}
          onChange={(v) => updateCell(record.classification, stage, v)}
          options={flatAccounts.map((a) => ({
            value: a.id,
            label: `${a.account_code} - ${a.account_name}`,
          }))}
        />
      ),
    })),
  ];

  const tableData = classifications.map((c) => ({ classification: c }));

  if (configLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <Title level={4} style={{ margin: 0 }}>Item classification – GL accounts</Title>
            <Text type="secondary">Configure default GL accounts by item type and transaction stage.</Text>
          </div>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saveMutation.isPending}
          >
            Save
          </Button>
        </div>
        <Table
          dataSource={tableData}
          columns={columns}
          rowKey="classification"
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );
};
